import { useState } from 'react';
import { Button, Segmented, Spin, Alert, Form, Space } from 'antd';
import { Tabs as AntTabs } from 'antd';
import type { TabsProps } from 'antd';
import { Box, Typography, Stack } from '@mui/material';
import { emphasize, styled } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  EditOutlined, TeamOutlined, SafetyCertificateOutlined, SearchOutlined, FileTextOutlined, PaperClipOutlined, IdcardOutlined,
} from '@ant-design/icons';
import Swal from 'sweetalert2';
import '../../../Styles/StyleHome.css';
import SectionCard from './SectionCard';
import SearchModal from '../../SearchModal';
import type ParamsPicklist from '../../../Types/ParamsPicklist';
import EditableValueField from './EditableValueField';
import KycDocumentsPanel from './KycDocumentsPanel';
import StagedDocumentsPicker, { type StagedFile } from './StagedDocumentsPicker';
import { uploadKycDocument } from './kycDocumentsApi';
import api from '../../../Api/axiosInstance';
import { fetchOne, encryptBody, parseEncryptedResponse } from './kycCrypto';
import Data from '../../../Data/Data.json';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import {
  CHAMPS_DATE_IDENTITE_KYC,
  CHAMPS_DOCUMENT_IDENTITE_KYC,
  CHAMPS_IDENTITE_PROCURATION,
  CHAMPS_IDENTITE_SIGNATAIRE,
  CHAMPS_IMAGE_IDENTITE_KYC,
  CHAMPS_RADIO_IDENTITE_KYC,
  GROUPES_CHAMPS_IDENTITE_PROCURATION,
  GROUPES_CHAMPS_IDENTITE_SIGNATAIRE,
  LABELS_CHAMP_IDENTITE_KYC,
  type ChampIdentiteKyc,
  type DemandeKycIdentiteDto,
  type ProcurationIdentiteRow,
  type SignataireIdentiteRow,
} from '../../../Models/DemandeKyc';

// Même logique que KycPage (champFieldProps) : image → vignette+upload, sexe
// → radio à 2 options (pas de saisie libre), date → vrai DatePicker (pas un
// texte libre où n'importe quoi passerait), document (pièces jointes d'une
// procuration) → image OU PDF/Word, le reste en texte simple.
function champFieldProps(champ: ChampIdentiteKyc, form: any) {
  if (CHAMPS_IMAGE_IDENTITE_KYC.includes(champ)) return { type: 'image' as const, form };
  if (CHAMPS_DOCUMENT_IDENTITE_KYC.includes(champ)) return { type: 'document' as const, form };
  const radioOptions = CHAMPS_RADIO_IDENTITE_KYC[champ];
  if (radioOptions) return { type: 'radio' as const, radioOptions, form };
  if (CHAMPS_DATE_IDENTITE_KYC.includes(champ)) return { type: 'date' as const };
  return {};
}

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor = theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': { backgroundColor: emphasize(backgroundColor, 0.06) },
    '&:active': { boxShadow: theme.shadows[1], backgroundColor: emphasize(backgroundColor, 0.12) },
  };
}) as typeof Chip;

type TypePersonne = 'SIGNATAIRE' | 'PROCURATION';

// Listes de sélection (même SearchModal que le client dans KycClientHeader) :
// l'utilisateur choisit un signataire / mandataire existant au lieu de saisir
// son code à la main. Champ du formulaire : CODE_SELECTION.
const CHAMP_SELECTION = 'CODE_SELECTION';
const searchSignataire: ParamsPicklist = { CodeAgence: '', Table: 'RELATION', Colonne1: 'CODREL', Colonne2: 'NOMREL', Colagence: '', Choix: 22 };
const searchProcuration: ParamsPicklist = { CodeAgence: '', Table: 'Procuration', Colonne1: 'Refprocur', Colonne2: 'nom', Colagence: '', Choix: 22 };

// Recherche directe (par CODREL/Refprocur, comme pour un client par NUMCLI) —
// pas de passage par client/compte, puisque modifier l'identité d'un
// signataire/mandataire n'a rien à voir avec un compte en particulier
// (retour utilisateur du 2026-10-09). Route confirmée par le backend
// (sp_GetKycAnnuaire, nouveau mode) : GetRelationByCodRel/
// GetProcurationByRefprocur pour la fiche.
function champValeur(champ: ChampIdentiteKyc, raw: unknown): [ChampIdentiteKyc, string] {
  let value = raw !== undefined && raw !== null ? String(raw) : '';
  if (CHAMPS_DATE_IDENTITE_KYC.includes(champ) && value) value = value.slice(0, 10);
  return [champ, value];
}

function ancienneValeursSignataire(f: SignataireIdentiteRow): Partial<Record<ChampIdentiteKyc, string>> {
  return Object.fromEntries([
    champValeur('REL_NOMREL', f.NomRel), champValeur('REL_PRENOM', f.Prenom), champValeur('REL_TITREL', f.TitRel),
    champValeur('REL_SEXE', f.Sexe), champValeur('REL_LIEUNAIS', f.LieuNais), champValeur('REL_DATENAIS', f.dateNais),
    champValeur('REL_CNI', f.cni), champValeur('REL_DATEXPIRCNI', f.DatExpirCni), champValeur('REL_ADRESREL', f.adresRel),
    champValeur('REL_TELREL', f.TelRel), champValeur('REL_FILIATION', f.filiation), champValeur('REL_AGCCPT', f.agccpt),
    champValeur('REL_PHOTO', f.Photo), champValeur('REL_SIGNATURE', f.Signature),
  ]);
}

function ancienneValeursProcuration(f: ProcurationIdentiteRow): Partial<Record<ChampIdentiteKyc, string>> {
  return Object.fromEntries([
    champValeur('PROC_NOM', f.nom), champValeur('PROC_PRENOM', f.prenom), champValeur('PROC_TITRE', f.titre),
    champValeur('PROC_SEXE', f.sexe), champValeur('PROC_LIEUNAIS', f.lieuNais), champValeur('PROC_DATENAIS', f.dateNais),
    champValeur('PROC_CNI', f.cni), champValeur('PROC_DATEXPIRCNI', f.datExpirCni), champValeur('PROC_TELPROCUR', f.TelProcur),
    champValeur('PROC_ADRESPROCUR', f.adresProcur), champValeur('PROC_FILIATION', f.filiation), champValeur('PROC_AGCCPT', f.agccpt),
    champValeur('PROC_PHOTO', f.Photo), champValeur('PROC_SIGNATURE', f.Signature), champValeur('PROC_AUTORITE', f.autorite),
    champValeur('PROC_DATDEBUT', f.datDebut), champValeur('PROC_DATFIN', f.DatFin), champValeur('PROC_DATEXPIRPROCUR', f.DatExpirProcur),
    champValeur('PROC_PIECEJ1', f.Piecej1), champValeur('PROC_PIECEJ2', f.Piecej2),
    champValeur('PROC_PLAFOND', f.Plafond),
  ]);
}

export default function KycIdentiteRelationPage() {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const [form] = Form.useForm();

  const [tab, setTab] = useState('0');
  const [type, setType] = useState<TypePersonne>('SIGNATAIRE');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fiche, setFiche] = useState<SignataireIdentiteRow | ProcurationIdentiteRow | null>(null);
  // « Modifier » bascule les champs en édition directement sur la page (comme
  // Client/Compte dans KycPage) — la soumission réelle passe par le bouton
  // « Enregistrer » du panneau Actions, pas un bouton local (même flow que
  // KycPage : le bouton « Modifier » du tableau Signataires actuels, lui,
  // reste sur la modale KycModifierIdentiteModal, cas différent).
  const [modeEdition, setModeEdition] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [docsDemandeIds, setDocsDemandeIds] = useState<(number | string)[]>([]);

  function changerType(v: string | number) {
    setType(v as TypePersonne);
    setCode('');
    form.setFieldsValue({ [CHAMP_SELECTION]: undefined });
    setFiche(null);
    setErreur(null);
    setModeEdition(false);
  }

  // Appelé par SearchModal après le choix dans la liste.
  function onSelection() {
    const choisi = form.getFieldValue(CHAMP_SELECTION);
    if (!choisi) return;
    setCode(String(choisi));
    rechercher(String(choisi));
  }

  async function rechercher(codeChoisi?: string) {
    const valeur = (codeChoisi ?? code).trim();
    if (!valeur) return;
    setLoading(true);
    setErreur(null);
    setFiche(null);
    setModeEdition(false);
    try {
      const resultat = type === 'SIGNATAIRE'
        ? await fetchOne<SignataireIdentiteRow>(Data.ApiUrl.GetRelationByCodRel, 'CypherCodRel', valeur)
        : await fetchOne<ProcurationIdentiteRow>(Data.ApiUrl.GetProcurationByRefprocur, 'CypherRefprocur', valeur);
      if (resultat.found && resultat.data) {
        setFiche(resultat.data);
      } else {
        setErreur(resultat.message || (type === 'SIGNATAIRE' ? 'Signataire introuvable.' : 'Procuration introuvable.'));
      }
    } finally {
      setLoading(false);
    }
  }

  // Annotation explicite nécessaire : sans elle, TS infère une union de deux
  // types tableau différemment paramétrés (ChampIdentiteSignataireKyc[] |
  // ChampIdentiteProcurationKyc[]), ce qui casse la résolution des surcharges
  // de .filter()/.map() ("Each member of the union type... has signatures,
  // but none of those are compatible") — même cause que dans
  // KycModifierIdentiteModal.tsx.
  const champs: ChampIdentiteKyc[] = type === 'SIGNATAIRE' ? CHAMPS_IDENTITE_SIGNATAIRE : CHAMPS_IDENTITE_PROCURATION;
  // Regroupés par thème pour l'affichage (une petite SectionCard par groupe),
  // comme Client/Compte dans KycPage — évite Photo/Signature écrasées à côté
  // de champs texte dans un unique bloc plat.
  const groupes = type === 'SIGNATAIRE' ? GROUPES_CHAMPS_IDENTITE_SIGNATAIRE : GROUPES_CHAMPS_IDENTITE_PROCURATION;
  const ancienneValeurs = fiche
    ? (type === 'SIGNATAIRE' ? ancienneValeursSignataire(fiche as SignataireIdentiteRow) : ancienneValeursProcuration(fiche as ProcurationIdentiteRow))
    : {};
  const codeFiche = fiche ? (type === 'SIGNATAIRE' ? (fiche as SignataireIdentiteRow).CODREL : (fiche as ProcurationIdentiteRow).Refprocur) : '';
  const nomAffiche = ancienneValeurs[type === 'SIGNATAIRE' ? 'REL_NOMREL' : 'PROC_NOM'];
  const prenomAffiche = ancienneValeurs[type === 'SIGNATAIRE' ? 'REL_PRENOM' : 'PROC_PRENOM'];

  // Appelé par le Form global (onFinish), déclenché uniquement par le bouton
  // « Enregistrer » du panneau Actions — même flow que onSubmitDemande dans
  // KycPage : documents agrafés (stagedFiles) envoyés juste après la création
  // de la demande, avec son IdDemande.
  async function onSubmitModification(values: any) {
    if (!fiche) return;

    const details = champs
      .filter((c) => {
        const nouvelle = values[`NouvelleValeur_${c}`];
        if (!nouvelle) return false;
        return String(nouvelle) !== String(ancienneValeurs[c] ?? '');
      })
      .map((c) => ({ Champ: c, NouvelleValeur: values[`NouvelleValeur_${c}`] }));

    if (details.length === 0) {
      Swal.fire({ title: 'Attention', text: 'Veuillez modifier au moins un champ (bouton « Modifier »).', icon: 'warning' });
      return;
    }

    const titre = type === 'SIGNATAIRE' ? "Modifier l'identité du signataire" : "Modifier l'identité du mandataire";
    const confirmation = await Swal.fire({
      title: titre,
      text: `Confirmez-vous l'envoi de ${details.length} champ(s) modifié(s) pour ${codeFiche} ?`,
      showCancelButton: true,
      color: 'green',
      confirmButtonText: 'Oui',
      confirmButtonColor: 'green',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'Non',
      showLoaderOnConfirm: true,
      icon: 'question',
    });
    if (!confirmation.isConfirmed) return;

    setBusy(true);
    try {
      const dto: DemandeKycIdentiteDto = {
        NUMCLI: fiche.NUMCLI,
        TypeRelation: type === 'SIGNATAIRE' ? 'SIGNATAIRE_MODIFICATION' : 'PROCURATION_MODIFICATION',
        CodRel: codeFiche,
        EnregistrePar: user.coduti,
        Details: details,
      };
      const response = await api.post(Data.ApiUrl.KycDemandeModificationIdentite, encryptBody(dto), { headers: { 'Content-Type': 'application/json' } });
      const payload = parseEncryptedResponse(response);
      if (payload && payload.StatutCode === 200) {
        const id = payload.Result;
        let echecsDocs = 0;
        for (const staged of stagedFiles) {
          const result = await uploadKycDocument(id, user.coduti, staged.file);
          if (!result.ok) echecsDocs += 1;
        }
        setDocsDemandeIds([id]);
        setStagedFiles([]);
        setModeEdition(false);
        setTab('1');
        Swal.fire({
          title: 'Succès',
          text: echecsDocs > 0
            ? `Demande n°${id} enregistrée. ${echecsDocs} document(s) n'ont pas pu être joints — réessayez depuis l'onglet Document.`
            : `Demande n°${id} enregistrée, en attente de validation.`,
          icon: echecsDocs > 0 ? 'warning' : 'success',
        });
      } else {
        Swal.fire({ title: 'Erreur', text: payload?.Message ?? 'La demande a été refusée.', icon: 'error' });
      }
    } catch (error: any) {
      const payload = parseEncryptedResponse(error?.response);
      Swal.fire({ title: 'Erreur', text: payload?.Message ?? "Impossible de soumettre la demande.", icon: 'error' });
    } finally {
      setBusy(false);
    }
  }

  function onFinishFailed({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) {
    Swal.fire({ title: 'Formulaire incomplet', text: `Veuillez renseigner les champs obligatoires (${errorFields.length}).`, icon: 'warning' });
  }

  function onActualiser() {
    form.resetFields();
    Swal.fire({ title: 'Actualisé', text: 'Le formulaire a été réinitialisé.', icon: 'success', timer: 1200, showConfirmButton: false });
  }

  function onAnnuler() {
    form.resetFields();
    setType('SIGNATAIRE');
    setCode('');
    form.setFieldsValue({ [CHAMP_SELECTION]: undefined });
    setFiche(null);
    setErreur(null);
    setModeEdition(false);
    setStagedFiles([]);
    setDocsDemandeIds([]);
    setTab('0');
  }

  const items: TabsProps['items'] = [
    {
      key: '0',
      label: <span><FileTextOutlined /> Demande</span>,
      children: (
        <Spin spinning={loading} size="large">
          <SectionCard icon={<SearchOutlined style={{ fontSize: 14 }} />} title="Signataire ou mandataire à modifier" color="#124b7a">
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Segmented
                className="segmentKyc"
                value={type}
                onChange={changerType}
                options={[{ label: 'Signataire', value: 'SIGNATAIRE' }, { label: 'Procuration', value: 'PROCURATION' }]}
              />
              <SearchModal
                key={type}
                Titre={type === 'SIGNATAIRE' ? 'Recherche signataire' : 'Recherche procuration'}
                label={type === 'SIGNATAIRE' ? 'Signataire' : 'Procuration'}
                name={CHAMP_SELECTION}
                Taille1="35%" Taille2="65%" width={520}
                data={[]}
                Parametre={type === 'SIGNATAIRE' ? searchSignataire : searchProcuration}
                notRequired
                form={form}
                exclureSiEgaux={type === 'SIGNATAIRE' ? ['IdClient', 'IdRel'] : undefined}
                ExecuteafterSelect1={onSelection}
              />
            </Box>
            {erreur && <Alert type="warning" showIcon message={erreur} style={{ marginTop: 16 }} />}
          </SectionCard>

          {fiche && (
            <Box sx={{ mt: 2 }}>
              <SectionCard
                icon={type === 'SIGNATAIRE' ? <TeamOutlined style={{ fontSize: 14 }} /> : <SafetyCertificateOutlined style={{ fontSize: 14 }} />}
                title={`${nomAffiche || ''} ${prenomAffiche || ''}`.trim() || codeFiche}
                color={type === 'SIGNATAIRE' ? '#2e7d32' : '#e65100'}
                action={
                  <Button icon={<EditOutlined />} onClick={() => setModeEdition((v) => !v)}>
                    {modeEdition ? 'Annuler la modification' : 'Modifier'}
                  </Button>
                }
              >
                {/* Exactement 2 blocs par ligne (gauche/droite) — pas auto-fit : avec
                    4 groupes (2 texte + Photo&Signature + Pièces jointes), un
                    conteneur large placerait les 4 sur une seule ligne, alors que
                    les 2 blocs média doivent rester groupés sous les 2 blocs
                    texte. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, alignItems: 'start' }}>
                  {groupes.map((groupe) => (
                    <SectionCard key={groupe.titre} icon={<IdcardOutlined style={{ fontSize: 14 }} />} title={groupe.titre} color="#124b7a">
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1 }}>
                        {groupe.champs.map((champ) => (
                          <EditableValueField
                            key={champ}
                            label={LABELS_CHAMP_IDENTITE_KYC[champ]}
                            name={`NouvelleValeur_${champ}`}
                            ancienneValeur={ancienneValeurs[champ] ?? ''}
                            editable={modeEdition}
                            {...champFieldProps(champ, form)}
                          />
                        ))}
                      </Box>
                    </SectionCard>
                  ))}
                </Box>
              </SectionCard>
            </Box>
          )}

          {!fiche && !erreur && !loading && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              Sélectionnez un signataire ou un mandataire dans la liste ci-dessus pour afficher sa fiche.
            </Typography>
          )}
        </Spin>
      ),
    },
    {
      key: '1',
      label: <span><PaperClipOutlined /> Document</span>,
      disabled: !fiche,
      children: docsDemandeIds.length > 0 ? (
        <Stack spacing={2}>
          {docsDemandeIds.map((id) => (
            <SectionCard key={id} icon={<PaperClipOutlined style={{ fontSize: 14 }} />} title={`Pièces jointes — demande n°${id}`} color="#2e7d32">
              <KycDocumentsPanel idDemande={id} uploadePar={user.coduti} />
            </SectionCard>
          ))}
          <Button onClick={onAnnuler}>Composer une nouvelle demande</Button>
        </Stack>
      ) : (
        <SectionCard icon={<PaperClipOutlined style={{ fontSize: 14 }} />} title="Documents justificatifs" subtitle="Ajoutés maintenant, ils seront envoyés dès la soumission de la demande (onglet Demande)" color="#0277bd">
          <StagedDocumentsPicker files={stagedFiles} onChange={setStagedFiles} />
        </SectionCard>
      ),
    },
  ];

  return (
    <div className="tab">
      <Box className="formTitle">
        <Breadcrumbs aria-label="breadcrumb">
          <StyledBreadcrumb component="a" href="/" label="Home" icon={<HomeIcon fontSize="small" />} />
          <StyledBreadcrumb component="a" href="#" label="KYC" />
          <StyledBreadcrumb label="Identité signataire / procuration" deleteIcon={<ExpandMoreIcon />} />
        </Breadcrumbs>
      </Box>

      <div className="clientP">
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          backgroundColor: 'var(--primaryColor)',
          color: 'var(--white)',
          padding: '8px 16px',
          borderRadius: 1,
          borderBottom: '5px solid var(--darkColor)',
          whiteSpace: 'nowrap',
        }}>
          <SafetyCertificateOutlined style={{ color: 'var(--white)' }} />
          <Typography fontWeight={600} sx={{ color: 'var(--white)' }}>Identité signataire / procuration</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              fontStyle="inherit"
              className="border"
              sx={{ p: 1, width: '100%', maxHeight: '75vh', overflowY: 'auto', boxSizing: 'border-box' }}
            >
              <Form form={form} labelAlign="left" autoComplete="off" onFinish={onSubmitModification} onFinishFailed={onFinishFailed}>
                <AntTabs
                  size="large" type="card" activeKey={tab} items={items}
                  onChange={(key) => { if (key !== '0' && !fiche) return; setTab(key); }}
                />
              </Form>
            </Box>
          </Box>

          <div
            className="bG"
            style={{
              flex: '0 0 220px',
              width: 220,
              position: 'relative',
              left: 'auto',
              top: 'auto',
              marginTop: 0,
              boxSizing: 'border-box',
            }}
          >
            <legend><Typography color="darkgreen">Actions</Typography></legend>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                size="large" className="bt_add" type="ghost" htmlType="button" block
                disabled={!fiche} loading={busy}
                onClick={() => form.submit()}
              >
                Enregistrer
              </Button>
              <Button
                size="large" className="bt_add" type="ghost" htmlType="button" block
                disabled={!fiche}
                onClick={onActualiser}
              >
                Actualiser
              </Button>
              <Button
                size="large" className="bt_add" type="ghost" htmlType="button" block
                onClick={onAnnuler}
              >
                Annuler
              </Button>
            </Space>
          </div>
        </Box>
      </div>
    </div>
  );
}
